import { types, flow, getRoot } from 'mobx-state-tree'
import { setCookie, destroyCookie } from 'nookies'
import Router from "next/router"

import User from './models/User'

import TRACKING, { resetUser } from '~/services/tracking'
import * as AuthService from '~/services/auth'
import * as BlockedService from '~/services/auth/blocked'

import * as LocalStorage from '~/utilities/localStorage'
import { getLanding } from '~/utilities/landingPage'
import LOGIN_VALIDATIONS from '~/utilities/loginValidations'
import objectIsEmpty from "~/utilities/objectIsEmpty"
import {
  buildEnrichedAuthReturnUrl,
  captureAuthPageState,
  restoreAuthPageState,
} from '~/utilities/authPageState'

export const AuthStore = types
  .model('AuthStore', {
    user: User,
    isAttemptingLogIn: types.optional(types.boolean, false),
    isAttemptingSignUp: types.optional(types.boolean, false),
    isAttemptingMagicLinkRequest: types.optional(types.boolean, false),
    magicLinkEmailSent: types.optional(types.boolean, false),
    loginCode: types.optional(types.string, ''),
    isUpdatingUserName: types.optional(types.boolean, false),
    isUpdatingPhoneNumber: types.optional(types.boolean, false),
    // Login fields
    email: types.optional(types.string, ''),
    password: types.maybe(types.optional(types.string, '')),

    // keys touched on step
    dirty: types.array(types.string),
    // stores errors from attempts to sign up or login
    displayErrors: types.array(
      types.frozen({
        // target: types.string,
        // message: types.string
      })
    ),
    shouldBeBlockedResult: types.optional(types.boolean, false),
    // Checkout sign-up has no password field (removed in 73872a373), so accounts are always
    // created with a temporary password; the pay button would otherwise stay blocked.
    canSignUpWithoutPassword: types.optional(types.boolean, true),
    // store UTM query parameters here if we came from ads but haven't logged a user in yet
    utmParameters: types.frozen({}),
    // GCLID (Google Click ID) of the ad click that brought this visitor, held here
    // whether or not they are logged in: the parking payment reads it from the store,
    // so it must not depend on the copy kept on the User.
    gclid: types.optional(types.string, ''),
    // When that click was first seen, so the backend can age-cap it against the
    // conversion window instead of assuming the click happened at payment time.
    gclidCapturedAt: types.optional(types.string, ''),
    // Facebook/Meta click id from the landing URL. Held on the auth store (not the
    // User) so the lookup socket can stamp it on FetcherSearchContactInfo for LKN
    // Close leads without requiring a Parse User column.
    fbclid: types.optional(types.string, ''),
    // store MSCLKID (Microsoft Click ID) for users from Microsoft Advertising before login
    msclkid: types.optional(types.string, ''),
    // User arrived with existing application state data which has been loaded into the store
    cameFromPersistedState: types.optional(types.boolean, false),
    /** True while post-auth parking funnel state is being rehydrated from URL/session. */
    isRestoringAuthPageState: types.optional(types.boolean, false),
  })
  .actions(self => ({
    afterSuccessfulAuth: flow(function* afterSuccessfulAuthGen(user, trackingFn) {
      self.setUser(user)
      yield self.user.fetchCurrentUserData()
      yield self.blockCheck()

      const updateKeys = []
      if (!objectIsEmpty(self.utmParameters)) {
        self.user.setUtmParameters({ ...self.utmParameters })
        updateKeys.push("utm")
      }
      if (self.gclid) {
        self.user.setGclid(self.gclid)
        updateKeys.push("gclid")
      }
      if (self.msclkid) {
        self.user.setMsclkid(self.msclkid)
        updateKeys.push("msclkid")
      }
      if (updateKeys.length) {
        self.user.updateUser(updateKeys, getLanding())
      }
      self.user.changeKey('accountCreatedWithRandomPassword', false)
      if (trackingFn) trackingFn()
      self.prepopulateLookupStore()
      if (typeof window !== 'undefined' && window.clarity) {
        window.clarity("identify", self.user.objectId)
      }
      yield restoreAuthPageState(getRoot(self))
      return true
    }),
    sessionTokenLogin: flow(function* sessionTokenLoginGen(sessionToken, ctx) {
      self.changeKey('isAttemptingLogIn', true)
      try {
        const user = yield AuthService.autoLogin(sessionToken)
        if (user) {
          self.setUser(user, ctx)
          self.user.changeKey('accountCreatedWithRandomPassword', false)
          yield self.blockCheck()
        }
      } catch (error) {
        self.unsetUser()
        if (process.env.WINIT_ENV !== 'test') {
          console.error('error session token login', error)
        }
      }
      self.changeKey('isAttemptingLogIn', false)
    }),
    signup: flow(function* signupGen({ addPaymentProcessor = false, locationState, acceptances } = {}) {
      self.user.resetTouched()
      self.user.resetDisplayErrors()
      const KEYS_TO_VALIDATE = [
        'name',
        'username',
        'phoneNumber',
        'email',
        'password',
        'userType',
      ]
      if (addPaymentProcessor) {
        KEYS_TO_VALIDATE.push('paymentProcessor')
      }

      yield self.user.validateRegistrationFields()
      const hasErrors = self.user.registrationFieldsHaveError
      const hasAsyncErrors = yield self.user.registrationFieldsHaveAsyncError()
      if (hasErrors || hasAsyncErrors) return false

      self.changeKey('isAttemptingSignUp', true)
      try {
        const createdUser = yield self.user.createNewUser({
          keys: KEYS_TO_VALIDATE,
          addPaymentProcessor,
          locationState,
          acceptances,
        })

        if (createdUser) {
          TRACKING.general.auth.signup()
          self.changeKey('isAttemptingSignUp', false)
          self.changeKey('isAttemptingLogIn', true)
          self.changeKey('email', self.user.email.toLowerCase().trim())
          self.changeKey('password', self.user.password)
          const didLogin = yield self.login()
          if (didLogin) {
            self.changeKey('isAttemptingLogIn', false)
            self.changeKey('password', '')
            return true
          }
        } else {
          // For pay and fight flow addPaymentProcessor is true so we want to be frictionless on the email in such case

          if (self.displayErrors.length > 0 && self.displayErrors[0].message === "This email is already registered." && addPaymentProcessor) {
              self.changeKey("isAttemptingSignUp", false)
              self.changeKey("email", self.user.email.toLowerCase().trim())
              return true
          }
          self.changeKey('isAttemptingSignUp', false)
        }
      } catch (error) {
        self.changeKey('isAttemptingSignUp', false)
        self.changeKey('isAttemptingLogIn', false)
        const asyncError = {
          message: error,
          target: 'asyncError'
        }

        self.setDisplayErrors([asyncError])
        if (process.env.WINIT_ENV !== 'test') {
          console.error('error with signup', error)
        }
      }
      return false
    }),
    login: flow(function* loginGen() {
      let success = false
      const { email, password } = self
      const hasErrors = self.validateStepTargetKeys(['email', 'password'])
      if (hasErrors.length) return false
      self.changeKey('isAttemptingLogIn', true)
      try {
        const user = yield AuthService.login(
          email.toLowerCase().trim(),
          password
        )
        if (user) {
          self.email = ''
          self.password = ''
          yield self.afterSuccessfulAuth(user, () => TRACKING.general.auth.winit())
          success = true
        }
      } catch (error) {
        const asyncError = {
          message: error,
          target: 'asyncError'
        }
        self.setDisplayErrors([asyncError])
        if (process.env.WINIT_ENV !== 'test') {
          console.error('error with login', error)
        }
        success = false
      }
      self.password = ''
      self.changeKey('isAttemptingLogIn', false)
      return success
    }),
    requestLoginMagicLink: flow(function* requestLoginMagicLinkGen(_returnUrl) {
      const hasErrors = self.validateStepTargetKeys(['email'])
      if (hasErrors.length) return false

      self.changeKey('isAttemptingMagicLinkRequest', true)
      self.changeKey('magicLinkEmailSent', false)
      try {
        const rootStore = getRoot(self)
        captureAuthPageState(rootStore)
        yield AuthService.requestLoginMagicLink(
          self.email.toLowerCase().trim(),
          buildEnrichedAuthReturnUrl(rootStore),
        )
        self.changeKey('magicLinkEmailSent', true)
        const { ui } = getRoot(self)
        if (ui.isPayNYCFlow) {
          TRACKING.paynycParking.magicLinkRequested(ui.isPayNYCFlow)
        }
        return true
      } catch (error) {
        const asyncError = {
          message: error,
          target: 'asyncError',
        }
        self.setDisplayErrors([asyncError])
        return false
      } finally {
        self.changeKey('isAttemptingMagicLinkRequest', false)
      }
    }),
    loginWithCode: flow(function* loginWithCodeGen() {
      let success = false
      const hasErrors = self.validateStepTargetKeys(['email', 'loginCode'])
      if (hasErrors.length) return false

      self.changeKey('isAttemptingLogIn', true)
      try {
        const user = yield AuthService.loginWithCode(
          self.email.toLowerCase().trim(),
          self.loginCode.trim(),
        )
        if (user) {
          const { ui } = getRoot(self)
          yield self.afterSuccessfulAuth(user, () => {
            TRACKING.general.auth.winit()
            if (ui.isPayNYCFlow) {
              TRACKING.paynycParking.codeLoginSuccess(ui.isPayNYCFlow)
            }
          })
          self.email = ''
          self.loginCode = ''
          success = true
        }
      } catch (error) {
        const asyncError = {
          message: error,
          target: 'asyncError',
        }
        self.setDisplayErrors([asyncError])
        success = false
      }
      self.changeKey('isAttemptingLogIn', false)
      return success
    }),
    magicLinkLogin: flow(function* magicLinkLoginGen(userObjectId, token) {
      self.changeKey('isAttemptingLogIn', true)
      try {
        const user = yield AuthService.magicLinkSignIn(userObjectId, token)
        if (user) {
          yield self.afterSuccessfulAuth(user, () => TRACKING.general.auth.winit())
          return true
        }
      } catch (error) {
        if (process.env.WINIT_ENV !== 'test') {
          console.error('error with magic link login', error)
        }
      } finally {
        self.changeKey('isAttemptingLogIn', false)
      }
      return false
    }),
    sendPhoneNumberVerificationCode: flow(function* sendPhoneNumberVerificationCode() {
      yield AuthService.sendPhoneNumberVerificationCode(self.user.sessionToken)
    }),
    checkPhoneNumberVerificationCode: flow(function* checkPhoneNumberVerificationCode(confirmationCode) {
      return yield AuthService.checkPhoneNumberVerificationCode(self.user.sessionToken, confirmationCode)
    }),
    googleLogin: flow(function* googleLoginGen(authData) {
      let success = false
      const passwordless = self.usePasswordlessLogin
      captureAuthPageState(getRoot(self))
      self.changeKey('isAttemptingLogIn', true)
      self.changeKey('isAttemptingSignUp', true)
      try {
        const user = yield AuthService.googleAuth(authData)
        if (user) {
          yield self.afterSuccessfulAuth(user, () => TRACKING.general.auth.google())
          success = true
        }
      } catch (error) {
        const message = typeof error === 'string' ? error : error?.message || String(error)
        const useMagicLink =
          passwordless &&
          (error?.code === 'USE_MAGIC_LINK' ||
            /already has a WinIt account/i.test(message) ||
            /email and password/i.test(message))
        const asyncError = {
          message: useMagicLink
            ? 'This email already has an account. Enter your email on the left and tap Continue — we will send a login link and 6-digit code.'
            : message,
          target: 'asyncError',
        }
        self.setDisplayErrors([asyncError])
        if (process.env.WINIT_ENV !== 'test') {
          console.error('error with google login', error)
        }
        success = false
      }
      if (success || !passwordless) {
        self.email = ''
      }
      self.password = ''
      self.changeKey('isAttemptingLogIn', false)
      self.changeKey('isAttemptingSignUp', false)
      return success
    }),
    facebookLogin: flow(function* facebookLoginGen(authData) {
      let success = false
      captureAuthPageState(getRoot(self))
      self.changeKey('isAttemptingLogIn', true)
      self.changeKey('isAttemptingSignUp', true)
      try {
        const user = yield AuthService.facebookAuth(authData)
        if (user) {
          yield self.afterSuccessfulAuth(user, () => TRACKING.general.auth.facebook())
          success = true
        }
      } catch (error) {
        const asyncError = {
          message: error,
          target: 'asyncError'
        }
        self.setDisplayErrors([asyncError])
        if (process.env.WINIT_ENV !== 'test') {
          console.error('error with facebook login', error)
        }
        success = false
      }
      self.email = ''
      self.password = ''
      self.changeKey('isAttemptingLogIn', false)
      self.changeKey('isAttemptingSignUp', false)
      return success
    }),
    blockCheck: flow(function* blockCheck() {
      try {
        const { user } = self
        if(user.isBlocked) {
          return true
        }
        const shouldBeBlocked = yield BlockedService.shouldBeBlocked(self.user.sessionToken, self.user.email)
        self.shouldBeBlockedResult = !!shouldBeBlocked
      } catch(error) {
        console.error('Error checking if user is blocked:', error)
      }
      return true
    }),
    setUser(user, ctx = {}) {
      self.user = user
      self.user.setLastUpdate()
      // Bind GA4 user_id (+ trackhome) as soon as the user is known -> LTV /
      // cross-device reporting for every logged-in session, not just checkout.
      TRACKING.identifyUser(user.objectId)
      LocalStorage.set('sessionToken', user.sessionToken)
      const options = {
        maxAge: 30 * 24 * 60 * 60, // 30 days
        path: '/'
      }
      if (process.env.WINIT_ENV !== 'development') {
        const { ui } = getRoot(self)
        options.domain = ui.currentHost ? `${ui.currentHost.replace('www','')}` : '.appwinit.com'
        options.secure = true
      }
      setCookie(ctx, 'sessionToken', user.sessionToken, options)
    },
    logout: flow(function* logoutGen() {
      try {
        yield AuthService.logout(self.user.sessionToken)
        self.unsetUser()
        resetUser()
      } catch (error) {
        console.error("Error logging out", error);
        self.showErrorToast(error);
      }
    }),
    prepopulateLookupStore () {
      const rootStore = getRoot(self)
      if (
        Router.pathname.includes('traffic/lookup') ||
        Router.pathname.includes('parking/flow') ||
        Router.pathname.includes('parking/pay') ||
        Router.pathname.includes('parking/submit') ||
        Router.pathname.includes('parking/results')
      ) {
        rootStore.trafficLookup.prepopulateUserInfo()
      }
    },
    unsetUser() {
      self.user = {}
      LocalStorage.remove('sessionToken')
      const options = {
        path: '/'
      }
      if (process.env.WINIT_ENV !== 'development') {
        const { ui } = getRoot(self)
        options.domain = ui.currentHost ? `${ui.currentHost.replace('www','')}` : '.appwinit.com'
      }
      destroyCookie({}, 'sessionToken', options)
    },
    /**
     * Adds a key to dirty keys array
     * @param {string} key The key which to mark as dirty
     */
    addTouched(key) {
      if (self.dirty && !self.dirty.includes(key)) {
        self.dirty.push(key)
      }
    },
    /**
     * Removes a specific key from dirty keys
     * @param {string} key Key which to remove
     */
    removeTouched(key) {
      self.dirty = self.dirty.filter(dirtyKey => dirtyKey !== key)
    },
    /**
     * Resets the dirty keys
     */
    resetTouched() {
      self.dirty = []
    },
    /**
     * Set display errors
     */
    setDisplayErrors(arr) {
      self.displayErrors = arr
      return self.displayErrors
    },
    setCanSignUpWithoutPassword(bool) {
      self.canSignUpWithoutPassword = bool
    },
    setIsUpdatingUserName(bool) {
      self.isUpdatingUserName = bool
    },
    setIsUpdatingPhoneNumber(bool) {
      self.isUpdatingPhoneNumber = bool
    },
    changeKey(key, value) {
      self.addTouched(key)
      self[key] = value
    },
    resetLogin() {
      self.resetTouched()
      self.setDisplayErrors([])
      self.email = ''
      self.password = ''
      self.loginCode = ''
      self.magicLinkEmailSent = false
    },
    validateStepTargetKeys(keys) {
      self.setDisplayErrors([])
      self.resetTouched()
      const errors = keys
        .map(key => ({
          message: self.validateKey(key),
          target: key
        }))
        .filter(err => err.message) // removes no errors
      self.setDisplayErrors(errors)
      return errors
    },
    showErrorToast(message) {
      const rootStore = getRoot(self)
      rootStore.ui.toasts.addToast({
        message:
          message ||
          `Sorry something unexpected happened. Please try again or contact us for further support.`,
        type: 'ERROR'
      })
    },
    setUtmParameters(utmParameters) {
      self.utmParameters = { ...utmParameters }
    },
    setGclid(gclid, capturedAt) {
      self.gclid = gclid || ''
      if (capturedAt) self.gclidCapturedAt = capturedAt
    },
    setFbclid(fbclid) {
      self.fbclid = fbclid || ''
    },
    setMsclkid(msclkid) {
      self.msclkid = msclkid || ''
    },
    setCameFromPersistedState(bool){
      self.cameFromPersistedState = bool
    },
  }))
  .views(self => ({
    get usePasswordlessLogin() {
      const { ui } = getRoot(self)
      return ui.usePasswordlessLogin || self.canSignUpWithoutPassword
    },
    get isLoggedIn() {
      return !!(
        self.user &&
        self.user.objectId &&
        self.user.objectId.length > 0
      )
    },
    get isUserBlocked() {
      return self.user.blockUser || self.shouldBeBlockedResult
    },
    validateKey(key) {
      if (LOGIN_VALIDATIONS[key]) {
        return LOGIN_VALIDATIONS[key](self[key])
      }
      return false
    },
    validateDisplayMessage(key) {
      const displayError = self.displayErrors.find(item => item.target === key)
      const isDirty = self.dirty.includes(key)
      if (!isDirty && displayError && displayError.message)
        return displayError.message
      // if (self.dirty.includes(key)) {
      //   return self.validateKey(key)
      // }
      return false
    }
  }))
